import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    TextInput,
    Switch,
    Group,
    Button,
    Textarea,
    PasswordInput,
    Select,
    NumberInput
} from '@mantine/core'
import { useForm, isNotEmpty, isEmail } from "@mantine/form";
import axios from 'axios';
import { CLIENT_ROUTE, API_ROUTE } from "./constants";
import { showMessage } from "../../lib/notification";
import type { IResponse } from "../../types/api";
import type { IArtist } from "./types";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import Page from "../../components/layout/Page";
import { artistTitles } from "./constants";
import { useCategoryNumber } from "../../hooks";
import { assignFiles } from "../../utils/form";

interface FormValues extends Pick<IArtist,
    'username'
    | 'password'
    | 'email'
    | 'name'
    | 'platformName'
    | 'description'
    | 'phoneNumber'
    | 'firstName'
    | 'lastName'
    | 'socialAccountType'
    | 'socialAccountName'
    | 'categoryNumber'
    | 'title'
    | 'bankAccountNumber'
    | 'bankAccountName'
    | 'isGroup'
    | 'isVerified'
    | 'isActive'
    | 'statArtistId'
    > {
    profilePicture: string | FileWithPath | null;
    coverPicture: string | FileWithPath | null;
}

interface IParams extends Omit<FormValues, 'profilePicture' | 'coverPicture'> {
    profilePicture?: string;
    coverPicture?: string;
}

const EditCategory = () => {

    const { state: id = null } = useLocation();

    const navigate = useNavigate();

    const categories = useCategoryNumber();

    const form = useForm<FormValues>({
        initialValues: {
            bankAccountName: "",
            bankAccountNumber: "",
            categoryNumber: 0,
            coverPicture: null,
            description: "",
            email: "",
            isGroup: false,
            isVerified: false,
            name: "",
            password: "",
            phoneNumber: "",
            platformName: "",
            profilePicture: null,
            title: "Дуучин",
            username: "",
            isActive: false,
            firstName: "",
            lastName: "",
            socialAccountName: "",
            socialAccountType: "",
            statArtistId: 0
        },
        validate: {
            name: isNotEmpty('Нэр оруулна уу'),
            email: isEmail('Имэйл хаяг буруу байна'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IArtist>>(`${API_ROUTE}/${id}`);
            form.setValues(data.result);
            form.setFieldValue('password', '');
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {
        const files = await assignFiles({
            profilePicture: values.profilePicture,
            coverPicture: values.coverPicture,
        }, 'artist');
        const params: IParams = {
            ...values,
            ...files
        }

        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green');
        } else {
            showMessage('Алдаа гарлаа');
        }
    }

    return (
        <Page>
            <Page.Header title={'Уран бүтээлч засах'} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <TextInput
                            label="Нэвтрэх нэр"
                            placeholder="Нэвтрэх нэр"
                            autoComplete='off'
                            {...form.getInputProps('username')}
                        />

                        <PasswordInput
                            placeholder="Нууц үг"
                            label="Нууц үг"
                            {...form.getInputProps('password')}
                        />

                        <TextInput
                            label="Имэйл"
                            placeholder="Имэйл"
                            {...form.getInputProps('email')}
                        />

                        <TextInput
                            withAsterisk
                            label="Тайзны нэр"
                            placeholder="Тайзны нэр"
                            {...form.getInputProps('name')}
                        />

                        <TextInput
                            withAsterisk
                            label="Овог"
                            placeholder="Овог"
                            {...form.getInputProps('lastName')}
                        />

                        <TextInput
                            withAsterisk
                            label="Нэр"
                            placeholder="Нэр"
                            {...form.getInputProps('firstName')}
                        />

                        <TextInput
                            label="Platform нэр"
                            placeholder="Platform нэр"
                            {...form.getInputProps('platformName')}
                        />

                        <Textarea
                            label="Тайлбар"
                            placeholder="Тайлбар"
                            {...form.getInputProps('description')}
                        />

                        <TextInput
                            label="Утасны дугаар"
                            placeholder="Утасны дугаар"
                            {...form.getInputProps('phoneNumber')}
                        />

                        <TextInput
                            label="Social хаягийн төрөл"
                            placeholder="Social хаягийн төрөл"
                            {...form.getInputProps('socialAccountType')}
                        />

                        <TextInput
                            label="Social хаяг"
                            placeholder="Social хаяг"
                            {...form.getInputProps('socialAccountName')}
                        />

                        <Select
                            withAsterisk
                            label='Ангилал дугаар'
                            data={categories}
                            {...form.getInputProps('categoryNumber')}
                        />

                        <Select
                            withAsterisk
                            label='Цол'
                            data={artistTitles}
                            {...form.getInputProps('title')}
                        />

                        <NumberInput
                            withAsterisk
                            label="Статистик ID"
                            placeholder="Статистик ID"
                            {...form.getInputProps('statArtistId')}
                        />

                        <TextInput
                            label="Банк"
                            placeholder="Банк"
                            {...form.getInputProps('bankAccountName')}
                        />

                        <TextInput
                            label="Дансны дугаар"
                            placeholder="Дансны дугаар"
                            {...form.getInputProps('bankAccountNumber')}
                        />

                        <FileUpload
                            value={form.values.profilePicture}
                            onChange={(value) => {
                                form.setFieldValue('profilePicture', value);
                            }}
                            label='Профайл зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <FileUpload
                            value={form.values.coverPicture}
                            onChange={(value) => {
                                form.setFieldValue('coverPicture', value);
                            }}
                            label='Ковер зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <Switch
                            label="Хамтлаг эсэх"
                            {...form.getInputProps('isGroup', { type: 'checkbox' })}
                        />

                        <Switch
                            label="Баталгаажсан эсэх"
                            {...form.getInputProps('isVerified', { type: 'checkbox' })}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditCategory;